/**
 * Hour tag rules — presets, floors and the readers both sides use. Hours accept
 * whole days only (a rolling 24/48-hour window would give partial hour buckets),
 * exactly like the Drop Generator's hour optimizer.
 * For Media Devoted | Created By Ciro Autiero
 */

import { DROP_GEN_HOUR_DATE_RANGES } from "../../drop-generator/hour-profile.types";

import type { HourPerformanceTagsResponse, HourTagVerdict } from "./hour-tags.types";

export const HOUR_TAG_EXCLUDED_PRESETS = ["last-24-hours", "last-48-hours"] as const;

export const HOUR_TAG_RULE_DATE_PRESETS = [...DROP_GEN_HOUR_DATE_RANGES, "custom"] as const;

export type HourTagRulePreset = (typeof HOUR_TAG_RULE_DATE_PRESETS)[number];

export function isHourTagPreset(preset: string): preset is HourTagRulePreset {
  return (HOUR_TAG_RULE_DATE_PRESETS as readonly string[]).includes(preset);
}

/** Unique visits an hour needs before a tag judges it (an hour under it is unknown). */
export const HOUR_TAG_DEFAULT_MIN_VOLUME = 100;
export const HOUR_TAG_MIN_VOLUME_MAX = 1_000_000;

/** The 24 hour keys "0".."23" every per-hour map uses. */
export const HOUR_TAG_HOUR_KEYS: readonly string[] = Array.from({ length: 24 }, (_, h) => String(h));

const UNAVAILABLE: HourTagVerdict = { state: "unknown", uniqueVisits: 0, reason: "unavailable" };
const NO_RULE: HourTagVerdict = { state: "unknown", uniqueVisits: 0, reason: "no_rule" };

/** The verdict of one tag on one hour; unknown when there is no response or the tag no longer exists. */
export function hourTagVerdict(
  resp: HourPerformanceTagsResponse | null | undefined,
  tag: string,
  hour: number,
): HourTagVerdict {
  if (!resp) return UNAVAILABLE;
  const byHour = resp.statesByTag[tag];
  if (!byHour || !resp.tagNames.includes(tag)) return NO_RULE;
  return byHour[String(hour)] ?? { state: "unknown", uniqueVisits: 0, reason: "no_data" };
}

/** Three-valued truth of "the hour carries this tag": true / false / null (unknown). */
export function hourTagTri(
  resp: HourPerformanceTagsResponse | null | undefined,
  tag: string,
  hour: number,
): boolean | null {
  const { state } = hourTagVerdict(resp, tag, hour);
  if (state === "tagged") return true;
  if (state === "not_tagged") return false;
  return null;
}

const fmtVisits = (n: number): string => n.toLocaleString("en-US");

/** Why a verdict is what it is, in words — empty for a plain tagged / not tagged hour. */
export function hourTagWhy(v: HourTagVerdict): string {
  switch (v.reason) {
    case "no_data":
      return "no visits in that hour";
    case "below_floor":
      return `${fmtVisits(v.uniqueVisits)} unique visits, under the tag's minimum`;
    case "report_failed":
      return "the tracker report failed";
    case "compare_missing":
      return "nothing to compare against";
    case "higher_tier_unknown":
      return "a higher tier of its group is unknown";
    case "higher_tier_tagged":
      return "a higher tier of its group took the hour";
    case "no_rule":
      return "no enabled hour tag rule has this name";
    case "unavailable":
      return "hour tags could not be read";
    default:
      return "";
  }
}
